import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import {
  usePartnerConfig,
  mergePartnerConfig,
  resolvePartnerSlug,
} from "../hooks/usePartnerConfig";
import { track, identify, getAnonymousId } from "../utils/rudderstack";
import { resolveUrlTemplate, getRedirectToken } from "../utils/urlTemplate";
import { safeSessionStorage } from "../utils/safeStorage";
import { resolveAttribution, saveAttributionData } from "../utils/attribution";
import { ExternalLink } from "lucide-react";

type DirectRedirectState = {
  countdown: number;
  hasRedirected: boolean;
};

const COUNTDOWN_SECONDS = 4;

export function DirectRedirectPageContent({
  config,
}: {
  config: LandingPageConfig;
}) {
  const [searchParams] = useSearchParams();
  const params = useParams<{ partnerSlug?: string; landingId?: string }>();
  const partnerConfig = usePartnerConfig();
  const [state, setState] = useState<DirectRedirectState>({
    countdown: COUNTDOWN_SECONDS,
    hasRedirected: false,
  });

  // Resolve attribution synchronously: the partner path slug is the durable signal,
  // query params (m, s, cp) enrich it, and stripped params are reconstructed from the
  // slug + landing config. Computed during render so the outbound redirect URL and the
  // linkage event carry the reconstructed values, not just what survived on the URL.
  const partnerSlug = resolvePartnerSlug(params, searchParams);
  const attribution = useMemo(
    () =>
      resolveAttribution(searchParams, {
        partnerSlug,
        defaultCampaign: config.defaultCampaign,
      }),
    [searchParams, partnerSlug, config.defaultCampaign],
  );

  // Persist attribution on mount, before the countdown-driven redirect can strip params.
  // DirectRedirectPage bypasses ExtendedLandingPage, so attribution must be captured here.
  useEffect(() => {
    saveAttributionData(attribution);

    if (attribution.source) {
      identify(undefined, {
        utm_source: attribution.source,
        utm_medium: attribution.medium || "affiliates",
        utm_campaign: attribution.campaign,
        utm_content: attribution.utmContent,
        utm_term: attribution.utmTerm,
      });
    }
  }, [attribution]);

  // Get RudderStack anonymous ID for attribution tracking
  // If RudderStack isn't initialized (dev mode), generate a session ID as fallback
  const getSessionId = () => {
    const rudderAnonymousId = getAnonymousId();
    if (rudderAnonymousId) {
      return rudderAnonymousId;
    }

    const sessionKey = `hm_session_${config.id}`;
    let sessionId = safeSessionStorage.getItem(sessionKey);
    if (!sessionId) {
      sessionId = `hm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      safeSessionStorage.setItem(sessionKey, sessionId);
    }
    return sessionId;
  };

  const anonymousId = getSessionId();
  const redirectToken = getRedirectToken(config.id);

  // Build redirect URL by replacing template variables
  const redirectUrl = config.externalRedirectUrl
    ? resolveUrlTemplate(config.externalRedirectUrl, {
        anonymousId,
        redirectToken,
        source: attribution.source,
        medium: attribution.medium,
        campaign: attribution.campaign,
        clickId: attribution.clickId,
        landingPageId: config.id,
      })
    : "https://app.healthmatch.io";

  const message =
    config.qualifiedRedirectMessage ||
    "We're directing you to the study website to complete your application.";

  useEffect(() => {
    track("lp.direct_redirect.viewed", {
      landing_page_id: config.id,
      external_redirect_url: config.externalRedirectUrl,
      countdown_seconds: COUNTDOWN_SECONDS,
      anonymous_id: anonymousId,
      redirect_url_with_tracking: redirectUrl,
      source: attribution.source,
    });

    // Dedicated linkage event for joining inbound affiliate clicks with outbound redirects in Redshift
    track("lp.redirect_attribution_linkage", {
      landing_page_id: config.id,
      // Inbound (affiliate side)
      click_id: attribution.clickId,
      inbound_source: attribution.source,
      inbound_campaign: attribution.campaign,
      inbound_medium: attribution.medium,
      match_type: attribution.matchType,
      ad_set_id: attribution.adSetId,
      ad_id: attribution.adId,
      // Outbound (trialandyou side)
      anonymous_id: anonymousId,
      redirect_token: redirectToken,
      outbound_url: redirectUrl,
      outbound_utm_campaign: `hm_lp_${redirectToken}`,
      // Metadata
      redirect_type: "direct_redirect",
      timestamp: new Date().toISOString(),
    });
  }, [config.id, config.externalRedirectUrl, anonymousId, redirectUrl, attribution]);

  // Handle countdown and redirect
  useEffect(() => {
    const { hasRedirected } = state;

    if (hasRedirected) {
      return;
    }

    const countdownInterval = setInterval(() => {
      setState((prev) => {
        if (prev.countdown <= 1 && !prev.hasRedirected) {
          clearInterval(countdownInterval);

          track("lp.direct_redirect.countdown_completed", {
            landing_page_id: config.id,
            destination: redirectUrl,
            anonymous_id: anonymousId,
          });

          track("lp.direct_redirect.redirected", {
            landing_page_id: config.id,
            destination_url: redirectUrl,
            trigger: "countdown",
            anonymous_id: anonymousId,
            source: attribution.source,
            timestamp: new Date().toISOString(),
          });

          // Small delay to ensure tracking completes before redirect
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 100);
          return { ...prev, countdown: 0, hasRedirected: true };
        }

        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [state, config.id, redirectUrl, anonymousId, attribution.source]);

  const handleManualRedirect = () => {
    track("lp.direct_redirect.manual_click", {
      landing_page_id: config.id,
      destination: redirectUrl,
      countdown_remaining: state.countdown,
      anonymous_id: anonymousId,
    });

    track("lp.direct_redirect.redirected", {
      landing_page_id: config.id,
      destination_url: redirectUrl,
      trigger: "manual_click",
      anonymous_id: anonymousId,
      source: attribution.source,
      timestamp: new Date().toISOString(),
    });

    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 100);
  };

  const countdownProgress = (state.countdown / COUNTDOWN_SECONDS) * 100;

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white min-h-[60vh] flex items-center" data-testid="direct-redirect-page">
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        <div
          className="relative z-10 isolation w-full max-w-lg mx-auto px-4 py-8 md:py-12"
          data-testid="direct-redirect-content"
        >
          <div
            className="bg-white rounded-2xl overflow-hidden p-6 md:p-8 flex flex-col items-center justify-center text-center shadow-lg border border-gray-100"
            data-testid="direct-redirect-card"
          >
            <div className="mb-4 text-primary-500">
              <ExternalLink className="h-12 w-12 mx-auto" strokeWidth={1.5} />
            </div>

            <h3
              className="text-xl md:text-2xl font-bold text-gray-900 mb-2"
              data-testid="direct-redirect-title"
            >
              {config.heroTitle}
            </h3>

            <p className="text-sm md:text-base text-gray-600 mb-6">
              {message}
            </p>

            <button
              onClick={handleManualRedirect}
              className="w-full max-w-xs inline-flex items-center justify-center px-6 py-3.5 border border-transparent rounded-lg text-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors mb-4"
              data-testid="direct-redirect-manual-button"
            >
              Continue Now &rarr;
            </button>

            <div className="w-full max-w-xs mb-1">
              <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-1000 ease-linear"
                  style={{ width: `${100 - countdownProgress}%` }}
                />
              </div>
            </div>

            <p
              className="text-xs text-gray-400 mb-4"
              data-testid="direct-redirect-countdown"
            >
              Auto-redirecting in {state.countdown}s
            </p>

            <p className="text-xs text-gray-400">
              You&apos;ll be redirected to a secure partner website.
            </p>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
